/* eslint-disable object-curly-newline, arrow-body-style,implicit-arrow-linebreak */
import { useMemoRef } from '@platform/react/hook'
import DecoratedText from 'ui/Element/Text/Decorated'
import { useMemo } from 'react'
import { getThemeColor, isArr, isStr } from 'lib/util'
import { useTheme } from '@mui/material'

/**
 * StatusText UI Element
 *
 * A {@link DecoratedText} for a specific status.
 *
 * @param {Object} p                  additional props
 * @param {string|Array} p.value      a string to be used as value
 * @param {string} p.variant          variant for the text
 * @param {Object} p.events           events for the component
 * @param {Function} p.events.onOpen  handler to fetch the status
 * @param {boolean} p.hidden          whether the element is hidden
 * @param {Object} p.decorators       additional decorators
 * @param {boolean} [p.success]       success decorator
 * @param {boolean} [p.warning]       warning decorator
 * @param {boolean} [p.error]         error decorator
 * @param {boolean} [p.list]          list decorator
 * @returns {?JSX.Element}            the new StatusText element
 */
const StatusText = ({
  value,
  variant,
  events,
  hidden = false,
  decorators,
  ...props
}) => {
  const theme = useTheme()
  const { success, warning, error, list } = props
  const [currentStatus = {}] = useMemo(() => (
    value && isArr(value) ? value : events?.onOpen?.(value) || [{}]
  ), [value, events])

  const targetColor = useMemo(() => (currentStatus?.color
    ? getThemeColor(theme, currentStatus.color)
    : undefined), [currentStatus])

  const targetValue = useMemo(() => currentStatus?.value
    || (isStr(value) && value)
    || null,
  [value, currentStatus])

  return hidden || !targetValue ? null : (
    <DecoratedText
      value={targetValue}
      color={targetColor}
      variant={variant}
      decorators={{
        uppercase: true,
        bold: true,
        list,
        ...!targetColor && {
          success,
          warning,
          error,
        },
        ...decorators,
      }}
    />
  )
}

export default useMemoRef(StatusText, props => [
  props.value,
  props.events,
  props.error,
  props.success,
  props.list,
  props.bold,
])
